import React, { useEffect, useState } from 'react';
import { ArrowRight, Mic, Square, Sparkles, Check, Edit2, PenLine } from 'lucide-react';
interface Props {
  onContinue: () => void;
}
type InputMode = 'voice' | 'text';
type RecordState = 'idle' | 'recording' | 'processing' | 'done';
export function OnboardingProjects({
  onContinue
}: Props) {
  const [inputMode, setInputMode] = useState<InputMode>('voice');
  const [recordState, setRecordState] = useState<RecordState>('idle');
  const [timer, setTimer] = useState(0);
  const [textInput, setTextInput] = useState('');
  const [mounted, setMounted] = useState(false);
  const [projectTitle] = useState('Piattaforma di tracciabilità per la filiera del cacao');
  const [projectDesc] = useState("Stiamo sviluppando un sistema basato su blockchain per garantire un compenso equo ai coltivatori in Costa d'Avorio, riducendo gli intermediari.");
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (recordState === 'recording') {
      interval = setInterval(() => setTimer(prev => prev + 1), 1000);
    } else {
      setTimer(0);
    }
    return () => clearInterval(interval);
  }, [recordState]);
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const handleMicClick = () => {
    if (recordState === 'idle') {
      setRecordState('recording');
    } else if (recordState === 'recording') {
      setRecordState('processing');
      setTimeout(() => setRecordState('done'), 2500);
    }
  };
  const handleTextSubmit = () => {
    if (textInput.trim().length < 10) return;
    setRecordState('processing');
    setTimeout(() => setRecordState('done'), 2500);
  };
  const handleModeSwitch = (mode: InputMode) => {
    if (recordState === 'idle') setInputMode(mode);
  };
  const isIdle = recordState === 'idle';
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] relative" data-id="element-279">
      <div className="flex-1 overflow-y-auto px-6 pt-12 pb-36" data-id="element-280">
        {/* Step Indicator */}
        <div className="flex justify-center gap-2 mb-10 transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(16px)',
        transitionDelay: '0ms'
      }} data-id="element-281">
          <div className="w-1.5 h-1.5 rounded-full bg-[#2A2A2A]" data-id="element-282" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#2A2A2A]" data-id="element-283" />
          <div className="w-6 h-1.5 rounded-full bg-[#C5D82D]" data-id="element-284" />
        </div>

        {/* Header */}
        <div className="mb-10 text-center transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(20px)',
        transitionDelay: '120ms'
      }} data-id="element-285">
          <h1 className="text-[24px] font-medium text-white mb-2 tracking-tight" data-id="element-286">
            Raccontaci un progetto
          </h1>
          <p className="text-[14px] text-[#888888] leading-relaxed max-w-[260px] mx-auto" data-id="element-287">
            L'AI lo trasformerà in una scheda per trovare le persone giuste.
          </p>
        </div>

        {/* Main Content Area */}
        <div className="flex flex-col items-center transition-all duration-700 ease-out" style={{
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: '250ms'
      }} data-id="element-288">
          {/* === IDLE STATE === */}
          {isIdle && <div className="w-full flex flex-col items-center" data-id="element-289">
              {/* Segmented Mode Toggle */}
              <div className="bg-[#141414] border border-[#2A2A2A] rounded-full p-1 flex mb-10 w-fit" data-id="element-290">
                <button onClick={() => handleModeSwitch('voice')} className={`flex items-center gap-2 px-5 py-2 rounded-full text-[13px] font-medium transition-all duration-200 ${inputMode === 'voice' ? 'bg-[#2A2A2A] text-white' : 'text-[#555555] hover:text-[#888888]'}`} data-id="element-291">
                  <Mic className="w-3.5 h-3.5" data-id="element-292" />
                  Voce
                </button>
                <button onClick={() => handleModeSwitch('text')} className={`flex items-center gap-2 px-5 py-2 rounded-full text-[13px] font-medium transition-all duration-200 ${inputMode === 'text' ? 'bg-[#2A2A2A] text-white' : 'text-[#555555] hover:text-[#888888]'}`} data-id="element-293">
                  <PenLine className="w-3.5 h-3.5" data-id="element-294" />
                  Testo
                </button>
              </div>

              {/* Voice Mode */}
              {inputMode === 'voice' && <div className="flex flex-col items-center" data-id="element-295">
                  <button onClick={handleMicClick} className="w-28 h-28 rounded-full bg-[#141414] border border-[#2A2A2A] flex items-center justify-center text-[#C5D82D] hover:border-[#C5D82D]/50 transition-all duration-300 active:scale-95 group relative" data-id="element-296">
                    <div className="absolute inset-0 rounded-full bg-[#C5D82D] opacity-0 group-hover:opacity-5 blur-2xl transition-opacity duration-300" data-id="element-297" />
                    <Mic className="w-10 h-10 relative z-10" strokeWidth={1.5} data-id="element-298" />
                  </button>
                  <p className="mt-5 text-[14px] font-medium text-white" data-id="element-299">
                    Tocca per registrare
                  </p>
                  <p className="mt-1 text-[12px] text-[#555555]" data-id="element-300">
                    Max 60 secondi
                  </p>
                </div>}

              {/* Text Mode */}
              {inputMode === 'text' && <div className="w-full space-y-4" data-id="element-301">
                  <textarea value={textInput} onChange={e => setTextInput(e.target.value)} placeholder="Es. Stiamo sviluppando una piattaforma per connettere piccoli agricoltori con mercati internazionali..." className="w-full bg-[#141414] border border-[#2A2A2A] rounded-2xl p-4 text-white placeholder:text-[#444444] focus:outline-none focus:border-[#C5D82D]/50 transition-colors text-[14px] leading-relaxed resize-none h-[180px]" data-id="element-302" />
                  <div className="flex items-center justify-between" data-id="element-303">
                    <p className="text-[12px] text-[#555555]" data-id="element-304">
                      {textInput.length > 0 ? `${textInput.length} caratteri` : 'Minimo 10 caratteri'}
                    </p>
                    <button onClick={handleTextSubmit} disabled={textInput.trim().length < 10} className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium transition-all duration-200 ${textInput.trim().length >= 10 ? 'bg-[#C5D82D] text-[#0D0D0D] active:scale-[0.97]' : 'bg-[#2A2A2A] text-[#555555] cursor-not-allowed'}`} data-id="element-305">
                      <Sparkles className="w-3.5 h-3.5" data-id="element-306" />
                      Genera
                    </button>
                  </div>
                </div>}
            </div>}

          {/* === RECORDING STATE === */}
          {recordState === 'recording' && <div className="flex flex-col items-center pt-4" data-id="element-307">
              <div className="relative" data-id="element-308">
                <div className="absolute inset-0 rounded-full border-2 border-[#D32F2F] animate-ping opacity-20" data-id="element-309" />
                <div className="absolute -inset-5 rounded-full border border-[#D32F2F] animate-pulse opacity-10" data-id="element-310" />
                <button onClick={handleMicClick} className="w-28 h-28 rounded-full bg-[#141414] border-2 border-[#D32F2F] flex items-center justify-center text-[#D32F2F] active:scale-95 transition-all relative z-10" data-id="element-311">
                  <Square className="w-8 h-8 fill-current" data-id="element-312" />
                </button>
              </div>
              <p className="mt-6 text-[20px] font-medium text-[#D32F2F] font-mono tracking-wider" data-id="element-313">
                {formatTime(timer)}
              </p>
              <p className="mt-2 text-[13px] text-[#888888]" data-id="element-314">
                Tocca per terminare
              </p>
            </div>}

          {/* === PROCESSING STATE === */}
          {recordState === 'processing' && <div className="flex flex-col items-center pt-4" data-id="element-315">
              <div className="w-28 h-28 rounded-full bg-[#141414] border border-[#2A2A2A] flex items-center justify-center relative" data-id="element-316">
                <Sparkles className="w-8 h-8 text-[#C5D82D] animate-pulse" data-id="element-317" />
                <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100" data-id="element-318">
                  <circle cx="50" cy="50" r="48" fill="none" stroke="#C5D82D" strokeWidth="1.5" strokeDasharray="301.59" strokeDashoffset="301.59" className="animate-[dash_2.5s_ease-in-out_forwards]" data-id="element-319" />
                </svg>
              </div>
              <p className="mt-6 text-[14px] font-medium text-white" data-id="element-320">
                L'AI lo analizzando...
              </p>
              <p className="mt-1 text-[12px] text-[#555555]" data-id="element-321">
                Creazione della scheda in corso
              </p>
            </div>}

          {/* === DONE STATE === */}
          {recordState === 'done' && <div className="w-full" data-id="element-322">
              {/* Generated Card */}
              <div className="bg-[#141414] border border-[#2A2A2A] rounded-2xl p-5 relative overflow-hidden" data-id="element-323">
                {/* Top accent line */}
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#C5D82D] to-transparent opacity-40" data-id="element-324" />

                <div className="flex items-center justify-between mb-4" data-id="element-325">
                  <div className="flex items-center gap-2" data-id="element-326">
                    <div className="w-5 h-5 rounded-full bg-[#C5D82D]/15 flex items-center justify-center" data-id="element-327">
                      <Sparkles className="w-3 h-3 text-[#C5D82D]" data-id="element-328" />
                    </div>
                    <span className="text-[11px] font-medium text-[#C5D82D] uppercase tracking-wider" data-id="element-329">
                      Generato dall'AI
                    </span>
                  </div>
                  <button className="w-7 h-7 rounded-full bg-[#1A1A1A] flex items-center justify-center text-[#555555] hover:text-white transition-colors" data-id="element-330">
                    <Edit2 className="w-3.5 h-3.5" data-id="element-331" />
                  </button>
                </div>

                <h3 className="text-[15px] font-medium text-white mb-2 leading-snug" data-id="element-332">
                  {projectTitle}
                </h3>
                <p className="text-[13px] text-[#888888] leading-relaxed mb-4" data-id="element-333">
                  {projectDesc}
                </p>

                <div className="flex flex-wrap gap-1.5" data-id="element-334">
                  {['Supply Chain', 'Blockchain', 'In corso'].map(tag => <span key={tag} className="px-2.5 py-1 rounded-full bg-[#1A1A1A] text-[#888888] text-[11px] font-medium" data-id="element-335">
                      {tag}
                    </span>)}
                </div>
              </div>

              {/* Add another */}
              <button onClick={() => {
            setRecordState('idle');
            setTextInput('');
          }} className="mt-5 mx-auto flex items-center gap-2 text-[13px] font-medium text-[#555555] hover:text-[#888888] transition-colors" data-id="element-336">
                <Mic className="w-3.5 h-3.5" data-id="element-337" />
                Aggiungi un altro progetto
              </button>
            </div>}
        </div>
      </div>

      {/* Fixed Bottom Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#0D0D0D]/95 backdrop-blur-xl border-t border-[#1A1A1A] px-6 pt-5 pb-8 transition-all duration-700 ease-out" style={{
      opacity: mounted ? 1 : 0,
      transform: mounted ? 'translateY(0)' : 'translateY(16px)',
      transitionDelay: '400ms'
    }} data-id="element-338">
        <button onClick={onContinue} className={`w-full rounded-full py-3.5 font-medium text-[14px] transition-all duration-200 flex items-center justify-center gap-2 ${recordState === 'done' ? 'bg-[#C5D82D] text-[#0D0D0D] active:scale-[0.98]' : 'bg-transparent border border-[#C5D82D] text-white active:scale-[0.98]'}`} data-id="element-339">
          Continua
          <ArrowRight className="w-4 h-4" data-id="element-340" />
        </button>
        {recordState !== 'done' && <button onClick={onContinue} className="w-full mt-3 py-1.5 text-[13px] font-medium text-[#555555] hover:text-[#888888] transition-colors text-center" data-id="element-341">
            Salta per ora
          </button>}
      </div>

      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes dash {
          to { stroke-dashoffset: 0; }
        }
      `
    }} data-id="element-342" />
    </div>;
}